import { useState } from "react";
import { Call02Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { useVoiceEnabled } from "providers/Auth";
import { MakeCallModal } from "./MakeCallModal";

interface MakeCallButtonProps {
  // Optional — omit when the destination is supplied via `toPhones`/`toName`
  // (e.g. a lead that isn't backed by a CRM contact).
  contactId?: string | number;
  leadId?: number;
  customerId?: number;
  // Destination phones/name when there's no CRM contact to derive them from.
  toPhones?: string[];
  toName?: string;
  // Disables the button (with an explanatory tooltip) when there's no phone.
  hasPhone?: boolean;
  className?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  // Ghost (no border/fill) styling for header quick-actions — requires BASE.
  borderLess?: boolean;
  // Compact icon-only trigger (e.g. next to a phone-number row in the inbox
  // Details panel) instead of the full "Make a call" text button. CRM-811.
  asIcon?: boolean;
  // Pre-select this destination number in the pre-dial modal (per-row dialing).
  initialToNumber?: string;
}

/**
 * "Make a call" launcher reused across the call entry points (CRM-811): the
 * lead/contact detail views and the inbox lead info pane. Renders nothing
 * unless the user is voice-enabled, then opens the shared pre-dial MakeCallModal.
 */
export const MakeCallButton = ({
  contactId,
  leadId,
  customerId,
  toPhones,
  toName,
  hasPhone = true,
  className,
  size = ButtonSize.SM,
  variant = ButtonVariant.PRIMARY,
  borderLess,
  asIcon = false,
  initialToNumber
}: MakeCallButtonProps) => {
  const isVoiceEnabled = useVoiceEnabled();
  const [open, setOpen] = useState(false);

  if (!isVoiceEnabled) return null;

  return (
    <>
      {asIcon ? (
        <IconButton
          HugeIcon={Call02Icon}
          variant={IconButtonVariant.TEXT}
          size={IconButtonSize.SM}
          disabled={!hasPhone}
          tooltip={hasPhone ? "Make a call" : "No phone number on file"}
          onClick={() => setOpen(true)}
          className={className}
        />
      ) : (
        <Button
          variant={variant}
          size={size}
          borderLess={borderLess}
          HugeIcon={Call02Icon}
          disabled={!hasPhone}
          tooltip={hasPhone ? undefined : "No phone number on file"}
          tooltipPlacement="top"
          onClick={() => setOpen(true)}
          className={className}
        >
          Make a call
        </Button>
      )}
      <MakeCallModal
        opened={open}
        onClose={() => setOpen(false)}
        contactId={contactId != null ? String(contactId) : ""}
        leadId={leadId}
        customerId={customerId}
        toPhones={toPhones}
        toName={toName}
        initialToNumber={initialToNumber}
      />
    </>
  );
};
